/**
 * OmegaDlmPage — Sprint 50, the DLM (Deterministic Language Mind) dashboard.
 *
 * Founder-only, English-only, responsive 375px+. Shows what DLM is composing,
 * the distribution across output languages, the determinism + traceability
 * quality, and lattice health — over the live /omega/dlm endpoints (poll ~20s).
 * Every panel leads with a plain-English summary sentence; raw fields stay
 * behind the detail rows.
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface LanguageWire {
  adapter_id: string;
  count: number;
}

interface KnownLangWire {
  adapter_id: string;
  label: string;
}

interface DlmSummary {
  total_compositions: number;
  languages: LanguageWire[];
  known_languages: KnownLangWire[];
  traceability_pct: number;
  determinism_pct: number;
}

interface DlmComposition {
  output_identity: string;
  querent: string;
  adapter_id: string;
  output_text: string;
  source_count: number;
  created_iso: string;
}

interface UnifiedConvergence {
  languages: { lang: string; adapter_id: string; atom_id: string; signature: string }[];
  atoms_in: number;
  atoms_out: number;
  unified_master_hash: string;
  unified_signed_entity: string;
}

/**
 * Rotating compose samples across the languages OMEGA knows, so the demo button
 * produces DISTINCT, multi-language compositions instead of the same English text.
 */
const SAMPLES: { lang: string; text: string }[] = [
  { lang: 'en', text: 'market opportunity fintech growth signal' },
  { lang: 'he', text: 'market opportunity fintech growth signal' },
  { lang: 'rust', text: 'fn deploy(score: i32) -> i32 { score + 1 }' },
  { lang: 'en', text: 'signed source atoms converge to one sealed output' },
  { lang: 'he', text: 'signed source atoms converge to one sealed output' },
  { lang: 'rust', text: 'struct Atom { kind: u8, signed: bool }' },
];

// ── Pure helpers (exported for tests) ─────────────────────────────────────────

/** Human label for an output-adapter id. */
export function languageLabel(adapterId: string): string {
  const LABELS: Record<string, string> = {
    'lang.en': 'English',
    'lang.he': 'Hebrew',
    'lang.ar': 'Arabic',
    'code.rust': 'Rust',
    'code.python': 'Python',
    'code.js': 'JavaScript',
    'code.ts': 'TypeScript',
    'biz.doc': 'Business',
    'mkt.copy': 'Marketing',
  };
  return LABELS[adapterId] ?? adapterId;
}

/**
 * Plain-English summary of the DLM's state (the lattice-health panel sentence).
 * Deterministic; states WHAT it did, WHY it matters, HOW MANY, CONFIDENCE, and
 * the recommended ACTION.
 */
export function buildDlmInsight(s: DlmSummary): string {
  const langCount = s.languages.length;
  if (s.total_compositions === 0) {
    return 'DLM has composed nothing yet. Confidence: low (no data). Action: run a composition to populate the lattice.';
  }
  const langWord = langCount === 1 ? 'language' : 'languages';
  const allClean = s.determinism_pct >= 100 && s.traceability_pct >= 100;
  const confidence = allClean ? 'high' : 'medium';
  const action = allClean
    ? 'none — determinism and traceability are at 100%.'
    : `review — determinism ${s.determinism_pct}%, traceability ${s.traceability_pct}%.`;
  return (
    `DLM composed ${s.total_compositions} output${s.total_compositions === 1 ? '' : 's'} ` +
    `across ${langCount} ${langWord}, every output traceable to signed source atoms. ` +
    `Based on ${s.total_compositions} compositions. Confidence: ${confidence}. Action: ${action}`
  );
}

/** Format an RFC-3339 timestamp for the activity column (English). */
export function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Data ─────────────────────────────────────────────────────────────────────

function useSummary() {
  return useQuery({
    queryKey: ['dlm', 'summary'],
    queryFn: () => apiFetch<DlmSummary>('/omega/dlm/summary'),
    refetchInterval: 20_000,
  });
}

function useCompositions() {
  return useQuery({
    queryKey: ['dlm', 'compositions'],
    queryFn: () => apiFetch<DlmComposition[]>('/omega/dlm/compositions'),
    refetchInterval: 20_000,
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--omega-surface, rgba(255,255,255,0.04))',
        border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
        borderRadius: 6,
        padding: '14px 16px',
        flex: '1 1 280px',
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: '8.5pt',
          color: 'var(--omega-ink-subtle)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function OmegaDlmPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const summary = useSummary();
  const compositions = useCompositions();
  const qc = useQueryClient();

  // Compose a clean, signed, traceable sample through the live DLM engine. Rotates
  // across the languages OMEGA knows (English / Hebrew / Rust) and varies the input
  // so successive composes are DISTINCT and MULTI-LANGUAGE — not the same English
  // text repeated (S45 QC #3). Founder-gated POST to the real /omega/dlm/compose.
  const compose = useMutation({
    mutationFn: () => {
      const i = (summary.data?.total_compositions ?? 0) % SAMPLES.length;
      return apiFetch('/omega/dlm/compose', { method: 'POST', body: SAMPLES[i] });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dlm', 'compositions'] });
      void qc.invalidateQueries({ queryKey: ['dlm', 'summary'] });
    },
  });

  // Converge ALL languages (English / Hebrew / Rust) into ONE unified, signed
  // domain-GI via the N→1 ConvergenceWorker — DLM as a true GI, not just composition.
  const converge = useMutation({
    mutationFn: () =>
      apiFetch<UnifiedConvergence>('/omega/dlm/converge', {
        method: 'POST',
        body: { text: 'market opportunity fintech growth' },
      }),
  });

  const s = summary.data;
  const comps = compositions.data ?? [];
  const unified = converge.data;

  return (
    <div
      className="omega-shell"
      style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}
    >
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'DLM' }]} />

      <div style={{ marginTop: 8, marginBottom: 6 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Deterministic Language Mind
        </h1>
        <div style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)', marginTop: 4 }}>
          Provable composition from signed atoms — no hallucination, fully traceable.
        </div>
      </div>

      {/* Human summary line */}
      {s && (
        <div
          style={{
            background: 'rgba(125,211,252,0.06)',
            border: '1px solid rgba(125,211,252,0.3)',
            borderRadius: 6,
            padding: '12px 16px',
            margin: '14px 0',
            fontSize: '9.5pt',
            lineHeight: 1.5,
          }}
        >
          {buildDlmInsight(s)}
        </div>
      )}

      {summary.isError && (
        <div style={{ color: '#f87171', fontSize: '9pt', margin: '10px 0' }}>
          Failed to load DLM summary.
        </div>
      )}

      {/* How DLM converges — always shown so the screen explains the mechanism */}
      <div
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
          borderRadius: 6,
          padding: '10px 16px',
          margin: '0 0 14px',
          fontSize: '9pt',
          color: 'var(--omega-ink-muted)',
          lineHeight: 1.5,
        }}
      >
        <strong style={{ color: 'var(--omega-ink)' }}>How it converges:</strong> every
        composition decomposes its input into <em>N signed source atoms</em>, then converges
        them into <em>ONE</em> content-addressed, sealed output — provable, no hallucination.
        Each output language (English / Hebrew / Rust) is realized by its own signed adapter.
      </div>

      {/* Panels */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
        <Panel title="Languages OMEGA knows">
          {!s || s.known_languages.length === 0 ? (
            <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>
              —
            </div>
          ) : (
            s.known_languages.map((l) => (
              <div
                key={l.adapter_id}
                style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9.5pt', padding: '3px 0' }}
              >
                <span>{l.label}</span>
                <span style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt' }}>{l.adapter_id}</span>
              </div>
            ))
          )}
        </Panel>

        <Panel title="Language distribution">
          {!s || s.languages.length === 0 ? (
            <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>
              No compositions yet.
            </div>
          ) : (
            s.languages.map((l) => (
              <div key={l.adapter_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9.5pt', padding: '3px 0' }}>
                <span>{languageLabel(l.adapter_id)}</span>
                <span style={{ color: 'var(--omega-ink-subtle)' }}>{l.count}</span>
              </div>
            ))
          )}
        </Panel>

        <Panel title="Quality">
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9.5pt', padding: '3px 0' }}>
            <span>Determinism</span>
            <span style={{ color: '#34d399', fontWeight: 700 }}>{s ? `${s.determinism_pct}%` : '—'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9.5pt', padding: '3px 0' }}>
            <span>Traceability</span>
            <span style={{ color: '#34d399', fontWeight: 700 }}>{s ? `${s.traceability_pct}%` : '—'}</span>
          </div>
        </Panel>

        <Panel title="Lattice health">
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9.5pt', padding: '3px 0' }}>
            <span>Compositions</span>
            <span style={{ color: 'var(--omega-ink-subtle)' }}>{s ? s.total_compositions : '—'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9.5pt', padding: '3px 0' }}>
            <span>Output languages</span>
            <span style={{ color: 'var(--omega-ink-subtle)' }}>{s ? s.languages.length : '—'}</span>
          </div>
        </Panel>
      </div>

      {/* Cross-language convergence — all languages → ONE signed entity */}
      <section style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
          <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Unified language convergence
          </div>
          <button
            type="button"
            onClick={() => converge.mutate()}
            disabled={converge.isPending}
            style={{
              fontSize: '8.5pt', fontWeight: 600, padding: '5px 12px', borderRadius: 5,
              border: '1px solid #7c3aed', cursor: converge.isPending ? 'default' : 'pointer',
              background: '#7c3aed', color: '#ffffff',
            }}
          >
            {converge.isPending ? 'Converging…' : 'Converge all languages → ONE'}
          </button>
        </div>
        <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-muted)', marginBottom: 8, lineHeight: 1.5 }}>
          Composes the same input in every language OMEGA knows, then merges all of them through
          the N→1 ConvergenceWorker into ONE signed unified entity — DLM as a true GI.
        </div>
        {converge.isError && (
          <div style={{ color: '#dc2626', fontSize: '9pt', marginBottom: 8 }}>Convergence failed — try again.</div>
        )}
        {unified && (
          <div
            style={{
              background: 'rgba(124,58,237,0.06)',
              border: '1px solid rgba(124,58,237,0.35)',
              borderRadius: 6,
              padding: '12px 16px',
              fontSize: '9pt',
              lineHeight: 1.6,
            }}
          >
            <div style={{ marginBottom: 6 }}>
              <strong style={{ color: 'var(--omega-ink)' }}>
                {unified.atoms_in} languages → ONE
              </strong>{' '}
              ({unified.atoms_out} atoms in the unified domain-GI)
            </div>
            {unified.languages.map((l) => (
              <div key={l.atom_id} style={{ display: 'flex', gap: 8, padding: '2px 0' }}>
                <span style={{ flexShrink: 0, width: 56, color: 'var(--omega-ink-subtle)' }}>{languageLabel(l.adapter_id)}</span>
                <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--omega-mono, monospace)', fontSize: '7.5pt', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--omega-ink-subtle)' }}>
                  {l.atom_id}
                </span>
              </div>
            ))}
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(124,58,237,0.25)' }}>
              <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt' }}>Unified master hash</div>
              <div style={{ fontFamily: 'var(--omega-mono, monospace)', fontSize: '7.5pt', wordBreak: 'break-all' }}>
                {unified.unified_master_hash}
              </div>
              <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt', marginTop: 6 }}>
                ONE signed merged entity
              </div>
              <div style={{ fontFamily: 'var(--omega-mono, monospace)', fontSize: '7.5pt', wordBreak: 'break-all', color: '#34d399' }}>
                {unified.unified_signed_entity}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Recent compositions */}
      <section>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
          <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Recent compositions
          </div>
          <button
            type="button"
            onClick={() => compose.mutate()}
            disabled={compose.isPending}
            style={{
              fontSize: '8.5pt', fontWeight: 600, padding: '5px 12px', borderRadius: 5,
              border: '1px solid var(--omega-accent, #2563eb)', cursor: compose.isPending ? 'default' : 'pointer',
              background: 'var(--omega-accent, #2563eb)', color: '#ffffff',
            }}
          >
            {compose.isPending ? 'Composing…' : 'Compose a sample'}
          </button>
        </div>
        {compose.isError && (
          <div style={{ color: '#dc2626', fontSize: '9pt', marginBottom: 8 }}>Compose failed — try again.</div>
        )}
        {compositions.isPending && (
          <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>
        )}
        {compositions.isError && (
          <div style={{ color: '#dc2626', fontSize: '9pt' }}>Failed to load compositions.</div>
        )}
        {!compositions.isPending && !compositions.isError && comps.length === 0 && (
          <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>
            No compositions yet — click "Compose a sample" to generate clean, signed, traceable prose from the lattice.
          </div>
        )}
        {comps.map((c) => (
          <div
            key={c.output_identity}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              padding: '8px 0',
              borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
            }}
          >
            <span style={{ flexShrink: 0, fontSize: '7.5pt', padding: '1px 6px', border: '1px solid var(--omega-rim, rgba(255,255,255,0.2))', borderRadius: 3, color: 'var(--omega-ink-subtle)' }}>
              {languageLabel(c.adapter_id)}
            </span>
            <span style={{ flex: 1, minWidth: 0, fontSize: '9.5pt', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.output_text}
            </span>
            <span style={{ flexShrink: 0, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
              {c.source_count} source{c.source_count === 1 ? '' : 's'}
            </span>
            <span style={{ flexShrink: 0, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
              {fmtTime(c.created_iso)}
            </span>
          </div>
        ))}
      </section>

      <div style={{ marginTop: 18 }}>
        <Link to="/omega" style={{ color: '#7dd3fc', fontSize: '8.5pt' }}>← OMEGA</Link>
      </div>
    </div>
  );
}
